/**
 * Blanko-LV Export (F4) — Angebots-PDF ohne Preise (EP/GP als Ausfüll-Linien),
 * z.B. als Anfrage oder zum handschriftlichen Ausfüllen. REINE Ausgabe: kein
 * Speichern, kein DB-Eintrag, keine KI-Optimierung; nutzt den aktuellen
 * Editor-Stand direkt (Snapshot wie ursprünglich in docx-export.tsx).
 *
 * Eigenständige Komponente im Stil von urkalkulation-export.tsx — herausgelöst
 * aus docx-export.tsx (Welle 1.3 UX-Entlastung), damit sie im "Export ▾"-Menü
 * der Angebots-Sidebar (quote-sidebar.tsx) gebündelt werden kann. Rendert
 * wahlweise als eigenständiger Button ODER (asMenuItem) als DropdownMenuItem.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { FileX2, Loader2 } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import { getQuoteFileStem } from '@shared/quote-naming';
import { normalizeVatRate } from '@/lib/quote-calculations';
import { capturePdfExportSnapshot } from '@/lib/pdf-export-snapshot';

interface PdfSettingsMeta {
  logoFilename?: string;
  agbText?: string;
}

interface BlankoLvExportProps {
  /** Als DropdownMenuItem rendern statt als eigenständiger Button (Export ▾-Menü). */
  asMenuItem?: boolean;
  /**
   * Externer In-Flight-Guard aus dem PERSISTENTEN Parent (Welle 1.3
   * Regression-Fix): das DropdownMenu unmountet den Menü-Content beim Schließen,
   * wodurch das interne `isGenerating` verloren ginge und ein laufender Export
   * beim erneuten Öffnen ein zweites Mal auslösbar wäre. `busy` (im Parent
   * gehalten) überlebt das Unmount und blockiert den Zweitklick; `onRunningChange`
   * meldet Start/Ende auch aus einer bereits unmounteten Instanz zuverlässig
   * an den Parent zurück (finally-Closure läuft weiter).
   */
  busy?: boolean;
  onRunningChange?: (running: boolean) => void;
}

export default function BlankoLvExport({ asMenuItem = false, busy = false, onRunningChange }: BlankoLvExportProps) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfSettingsMeta, setPdfSettingsMeta] = useState<PdfSettingsMeta | null>(null);

  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const gewerkOrder = useOfferStore((state) => state.gewerkOrder);
  const discountAmount = useOfferStore((state) => state.discountAmount);
  const discountType = useOfferStore((state) => state.discountType);
  const discountApplicationMode = useOfferStore((state) => state.discountApplicationMode);
  const isDiscountAppliedToPrices = useOfferStore((state) => state.isDiscountAppliedToPrices);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/pdf-settings');
        if (response.ok) {
          const data = await response.json();
          if (!cancelled && data.success) setPdfSettingsMeta(data.data || null);
        }
      } catch (error) {
        console.error('Failed to load PDF settings:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExport = async () => {
    // Doppelklick-/Doppelrequest-Schutz: läuft bereits ein Export (lokal ODER
    // laut Parent-Guard), NICHT erneut starten.
    if (busy || isGenerating) return;
    try {
      onRunningChange?.(true);
      setIsGenerating(true);
      const snapshot = capturePdfExportSnapshot({
        header: quoteHeader,
        items: quoteItems,
        gewerkOrder,
        options: {
          agbText: pdfSettingsMeta?.agbText && pdfSettingsMeta.agbText.trim().length > 0
            ? pdfSettingsMeta.agbText
            : undefined,
          discountAmount,
          discountType,
          discountApplicationMode,
          isDiscountAppliedToPrices,
          vatRate: normalizeVatRate(quoteHeader.mwstSatz),
          showPrices: false,
        },
      });

      const response = await fetch('/api/generate-pdf-accurate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: snapshot.header,
          items: snapshot.items,
          gewerkOrder: snapshot.gewerkOrder,
          options: snapshot.options,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server returned ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${getQuoteFileStem({ header: snapshot.header }, 'Angebot')}_Blanko-LV.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Blanko-LV erstellt',
        description: `${snapshot.items.length} Position(en) ohne Preise exportiert.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Blanko-LV-Fehler', description: message, variant: 'destructive' });
    } finally {
      setIsGenerating(false);
      onRunningChange?.(false);
    }
  };

  const disabled = busy || isGenerating || quoteItems.length === 0;

  if (asMenuItem) {
    return (
      <DropdownMenuItem
        onSelect={() => {
          // KEIN preventDefault: das ist eine einmalige Aktion, kein Dialog-
          // Trigger — das Menü darf nach der Auswahl normal schließen.
          handleExport();
        }}
        disabled={disabled}
        data-testid="button-export-blanko-lv"
        title="Angebots-PDF ohne Preise: EP/GP als Ausfüll-Linien, keine Summen — z.B. als Anfrage oder zum handschriftlichen Ausfüllen"
      >
        {isGenerating ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <FileX2 className="mr-2 h-4 w-4" />
        )}
        Blanko-LV (ohne Preise)
      </DropdownMenuItem>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={disabled}
      className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
      data-testid="button-export-blanko-lv"
      title="Angebots-PDF ohne Preise: EP/GP als Ausfüll-Linien, keine Summen — z.B. als Anfrage oder zum handschriftlichen Ausfüllen"
    >
      {isGenerating ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Blanko-LV wird erstellt...
        </>
      ) : (
        <>
          <FileX2 className="mr-2 h-4 w-4" />
          Blanko-LV (ohne Preise)
        </>
      )}
    </Button>
  );
}
